import { useCallback, useState } from 'react'
import {
  type Instacart_Catalog_Admin_V1_TempOverrideEntityInput_Input,
  useCreateTempOverrideMutation,
} from '../../../../__codegen__/api'

const TEMP_OVERRIDE_BATCH_SIZE = 100

type TempOverrideInput = Instacart_Catalog_Admin_V1_TempOverrideEntityInput_Input

const TEMP_OVERRIDE_ATTRS = [
  { attribute: '{"available": false}' },
  { attribute: '{"in_assortment": false}' },
]

const TEMP_OVERRIDE_ATTRS_TOMBSTONE = [
  { attribute: '{"available": false}', tombstone: true },
  { attribute: '{"in_assortment": false}', tombstone: true },
]

const createTempOverrideAttrs = (tombstone: boolean) =>
  tombstone ? TEMP_OVERRIDE_ATTRS_TOMBSTONE : TEMP_OVERRIDE_ATTRS

const buildTempOverrideInput = ({
  retailerId,
  productId,
  localeId,
  surfaceId,
  itemId,
  tombstone = false,
}: {
  retailerId: string
  productId: string
  localeId: number
  surfaceId: number
  itemId: number
  tombstone?: boolean
}): TempOverrideInput => ({
  entityId: {
    retailerId: retailerId,
    productId: productId,
    localeId: localeId.toString(),
    surfaceId: surfaceId.toString(),
    itemId: itemId.toString(),
  },
  tempOverrideAttrs: createTempOverrideAttrs(tombstone),
})

const chunkInputs = (inputs: TempOverrideInput[], chunkSize: number) => {
  if (chunkSize <= 0) return [inputs]

  const chunks: TempOverrideInput[][] = []
  for (let i = 0; i < inputs.length; i += chunkSize) {
    chunks.push(inputs.slice(i, i + chunkSize))
  }
  return chunks
}

export const useCreateTempOverride = () => {
  const [createTempOverride] = useCreateTempOverrideMutation()
  const [mutationState, setMutationState] = useState<{
    called: boolean
    loading: boolean
    error: Error | null
    totalCount: number
    successCount: number
  }>({
    called: false,
    loading: false,
    error: null,
    totalCount: 0,
    successCount: 0,
  })

  const createTempOverrideCall = useCallback(
    async (
      productId: string,
      retailerId: string,
      localeId: number,
      surfaceId: number,
      itemIds: number[],
      tombstoneItemIds: number[]
    ) => {
      const activeInputs = itemIds.map(itemId =>
        buildTempOverrideInput({ retailerId, productId, localeId, surfaceId, itemId })
      )
      const tombstonedInputs = tombstoneItemIds.map(itemId =>
        buildTempOverrideInput({
          retailerId,
          productId,
          localeId,
          surfaceId,
          itemId,
          tombstone: true,
        })
      )

      const allInputs = [...activeInputs, ...tombstonedInputs]

      if (allInputs.length === 0) {
        setMutationState(prev => ({
          ...prev,
          called: true,
          loading: false,
          error: null,
          totalCount: 0,
          successCount: 0,
        }))
        return
      }

      setMutationState({
        called: true,
        loading: true,
        error: null,
        totalCount: allInputs.length,
        successCount: 0,
      })

      const batches = chunkInputs(allInputs, TEMP_OVERRIDE_BATCH_SIZE)

      const batchPromises = batches.map(batch =>
        createTempOverride({
          variables: {
            data: batch,
          },
        })
          .then(() => ({
            status: 'fulfilled' as const,
            processed: batch.length,
          }))
          .catch(error => ({
            status: 'rejected' as const,
            processed: batch.length,
            error: error instanceof Error ? error : new Error('Temp override failed'),
          }))
      )

      const results = await Promise.all(batchPromises)

      const successes = results.filter(result => result.status === 'fulfilled')
      const failures = results.filter(
        (result): result is { status: 'rejected'; processed: number; error: Error } => result.status === 'rejected'
      )

      const successCount = successes.reduce((sum, result) => sum + result.processed, 0)

      if (failures.length === 0) {
        setMutationState({
          called: true,
          loading: false,
          error: null,
          totalCount: allInputs.length,
          successCount: successCount,
        })
        return
      }

      const aggregatedError =
        failures.length === results.length
          ? failures[0].error
          : new Error('Some temporary availability overrides failed')

      setMutationState({
        called: true,
        loading: false,
        error: aggregatedError,
        totalCount: allInputs.length,
        successCount: successCount,
      })
    },
    [createTempOverride]
  )

  return {
    createTempOverrideCall,
    loading: mutationState.loading,
    called: mutationState.called,
    error: mutationState.error,
    totalCount: mutationState.totalCount,
    successCount: mutationState.successCount,
  }
}
